import { useContext, useEffect, useState } from 'react';
import { Context as OptimizelyContext } from '../OptimizelyProvider';

type Config = { disableTracking?: boolean };

const useClientLoader = (isContextLoading: boolean) => {
    const [isMounted, setIsMounted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isLoading) return;
        const isLoaded = isMounted && !isContextLoading;
        setIsLoading(!isLoaded);
    }, [isMounted, isContextLoading, isLoading]);

    return isLoading;
};

/**
 * Returns state of Optimizely flag.
 * **Warning: If using on a client only page this value may change, use the
 * lazy variant of this hook to avoid this : {@link useLazyOptimizelyFeatureFlag}**.
 * Will activate the feature; i.e emit a GA and ContentSquare Event
 * @see {@link https://gitlab.com/zoopla/frontend/app-react/-/blob/main/docs/OPTIMIZELY.md#hooks}
 */
export const useOptimizelyFeatureFlag = (
    featureFlagName: string,
    config: Config = { disableTracking: false }
): [isEnabled: boolean] => {
    const { features, activate } = useContext(OptimizelyContext);
    const isEnabled = featureFlagName in features;

    useEffect(() => {
        if (!config.disableTracking && isEnabled) {
            activate({
                type: 'activate',
                payload: {
                    experiment: featureFlagName,
                    variation: isEnabled.toString()
                }
            });
        }
    }, [isEnabled, config, featureFlagName, activate]);

    return [isEnabled];
};

/**
 * Lazy variant of {@link useOptimizelyFeatureFlag} hook
 * @see {@link https://gitlab.com/zoopla/frontend/app-react/-/blob/main/docs/OPTIMIZELY.md#lazy-hooks-client-side-only}
 */
export const useLazyOptimizelyFeatureFlag = (
    featureFlagName: string,
    config?: Config
): [isLoading: boolean, isEnabled: boolean | null] => {
    const { features, loading, activate } = useContext(OptimizelyContext);
    const isLoading = useClientLoader(loading);
    const isEnabled = featureFlagName in features;

    useEffect(() => {
        if (!config?.disableTracking && !isLoading && isEnabled) {
            activate({
                type: 'activate',
                payload: {
                    experiment: featureFlagName,
                    variation: isEnabled.toString()
                }
            });
        }
    }, [isEnabled, config, featureFlagName, isLoading, activate]);

    if (isLoading) return [isLoading, null];
    return [isLoading, isEnabled];
};

/**
 * Calls {@link useOptimizelyFeatureFlag} and disables tracking based on qualifier status.
 *
 * @param featureFlagName key of feature flag you'd like to check against Optimizely datafile
 * @param qualifier boolean variable that determines whether feature flag should be tracked
 * @see {@link https://gitlab.com/zoopla/frontend/app-react/-/blob/main/docs/OPTIMIZELY.md#hooks}
 */

export const useQualifiedOptimizelyFeatureFlag = (
    featureFlagName: string,
    qualifier: boolean,
    config: Config = { disableTracking: false }
): [isEnabled: boolean] => {
    const [isEnabled] = useOptimizelyFeatureFlag(featureFlagName, {
        ...config,
        disableTracking: !qualifier
    });

    return qualifier ? [isEnabled] : [false];
};

/**
 * Returns state of Optimizely Experiment.
 * **Warning: If using on a client only page this value may change, use the
 * lazy variant of this hook to avoid this : {@link useLazyOptimizelyExperiment}**.
 * Will activate the experiment; i.e emit a GA and ContentSquare Event
 * * @see {@link https://gitlab.com/zoopla/frontend/app-react/-/blob/main/docs/OPTIMIZELY.md#hooks}
 */
export const useOptimizelyExperiment = (
    experimentKey: string,
    config: Config = { disableTracking: false }
): [variation: string | null] => {
    const { experiments, activate } = useContext(OptimizelyContext);
    const variant = experiments[experimentKey] ?? null;

    useEffect(() => {
        if (variant && !config.disableTracking) {
            activate({
                type: 'activate',
                payload: { experiment: experimentKey, variation: variant }
            });
        }
    }, [variant, config, experimentKey, activate]);

    return [variant];
};

/**
 * Lazy variant of {@link useOptimizelyExperiment} hook
 * @see {@link https://gitlab.com/zoopla/frontend/app-react/-/blob/main/docs/OPTIMIZELY.md#lazy-hooks-client-side-only}
 */
export const useLazyOptimizelyExperiment = (
    experimentKey: string,
    config?: Config
): [isLoading: boolean, variation: string | null] => {
    const { experiments, loading, activate } = useContext(OptimizelyContext);
    const isLoading = useClientLoader(loading);
    const variant = experiments[experimentKey] ?? null;

    useEffect(() => {
        if (variant && !config?.disableTracking && !isLoading) {
            activate({
                type: 'activate',
                payload: { experiment: experimentKey, variation: variant }
            });
        }
    }, [isLoading, variant, config, experimentKey, activate]);

    if (isLoading) return [isLoading, null];
    return [isLoading, variant];
};

/**
 * Calls {@link useOptimizelyExperiment} and disables tracking based on qualifier status.
 *
 * @param experimentKey key of experiment you'd like to check against Optimizely datafile
 * @param qualifier boolean variable that determines whether experiment should be tracked
 * @see {@link https://gitlab.com/zoopla/frontend/app-react/-/blob/main/docs/OPTIMIZELY.md#hooks}
 */

export const useQualifiedOptimizelyExperiment = (
    experimentKey: string,
    qualifier: boolean,
    config: Config = { disableTracking: false }
): [variation: string | null] => {
    const [variation] = useOptimizelyExperiment(experimentKey, {
        ...config,
        disableTracking: !qualifier
    });

    return qualifier ? [variation] : [null];
};
